import { Modal } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { useFetchCountriesQuery, useFetchStatesQuery } from 'common/store/api/rootApi'
import { IntakeFormsDialog } from 'features/Procedure/components/intake-forms-dialog'
import type { Patient } from 'features/Procedure/state/procedure-api.type'
import { useUpdatePatientContactMutation } from 'features/Procedure/state/procedure.api'

import { PatientContactForm } from './patient-contact-form'
import {
  buildInitialContactValues,
  toContactPayload,
  type PatientContactFormValues,
} from './patient-contact-form.schema'
import styles from './patient-info-dialog.module.scss'
import { PatientInfoView } from './patient-info-view'
import { PhysicianIntroductions } from './physician-introductions'

type Props = {
  isOpen: boolean
  onClose: () => void
  patientInfo: Patient | null
}

const DIALOG_WIDTH = 749

export const PatientInfoDialog = ({ isOpen, onClose, patientInfo }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()
  const [isEditing, setIsEditing] = useState(false)
  const [isIntakeFormsOpen, setIsIntakeFormsOpen] = useState(false)
  const [contactOverride, setContactOverride] = useState<Partial<Patient> | null>(null)
  const [updatePatientContact, { isLoading: isSaving }] = useUpdatePatientContactMutation()

  useFetchCountriesQuery()
  useFetchStatesQuery()

  const patientId = patientInfo?.patient_id

  useEffect(() => {
    setIsEditing(false)
    setContactOverride(null)
    setIsIntakeFormsOpen(false)
  }, [patientId])

  // Keep contactOverride across close/reopen: the parent's patient payload may
  // be stale until refetched, and the override is cleared on patient change.
  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false)
    }
  }, [isOpen])

  const displayPatient = useMemo(() => {
    if (!patientInfo) return null
    return contactOverride ? { ...patientInfo, ...contactOverride } : patientInfo
  }, [patientInfo, contactOverride])

  if (!displayPatient || !patientId) return null

  const handleSubmit = async (values: PatientContactFormValues) => {
    if (!siteId) return

    const payload = toContactPayload(values)

    try {
      await updatePatientContact({ siteId, patientId, data: payload }).unwrap()
      setContactOverride(payload)
      setIsEditing(false)
      setNotification({
        type: ALERT_CONSTANTS.SUCCESS,
        duration: 2,
        title: 'Saved',
        description: 'Patient details updated',
      })
    } catch (error: unknown) {
      const description =
        error instanceof Error ? error.message : 'Failed to update patient details'
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description,
      })
    }
  }

  return (
    <Modal
      open={isOpen}
      onCancel={isIntakeFormsOpen ? undefined : onClose}
      footer={null}
      width={DIALOG_WIDTH}
      centered
      maskClosable={false}
      classNames={{
        content: styles.modalContent,
      }}>
      <div className={styles.scrollArea}>
        {isEditing ? (
          <PatientContactForm
            initialValues={buildInitialContactValues(displayPatient)}
            isSaving={isSaving}
            onBack={() => setIsEditing(false)}
            onSubmit={handleSubmit}
          />
        ) : (
          <>
            <PatientInfoView
              patientInfo={displayPatient}
              onEdit={() => setIsEditing(true)}
              onOpenIntakeForms={() => setIsIntakeFormsOpen(true)}
            />
            {siteId && (
              <PhysicianIntroductions siteId={siteId} patientId={patientId} isOpen={isOpen} />
            )}
          </>
        )}
      </div>
      {patientId && (
        <IntakeFormsDialog
          isOpen={isIntakeFormsOpen}
          onClose={() => setIsIntakeFormsOpen(false)}
          patientId={patientId}
        />
      )}
    </Modal>
  )
}
